import { useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { useRequestOnMount } from "../../common/hooks/useRequestOnMount"
import { subscriptionService } from "../../data/services/subscriptionService"
import base64 from "@common/utils/base64"
import { notifyBugsnag } from "@common/utils/initBugsnag"

// L'accueil parent ne redemande ses tarifs qu'une fois par identité : deux
// relances couvrent la course ; au-delà, la page attend et Bugsnag le dit.
export const MAX_REFETCHES = 2

/**
 * Les pages ouvertes depuis un lien (paiement d'une vente, funnel) montrent
 * l'élève NOMMÉ par le lien. Or `getSubscribePricing` remplit un seul tiroir,
 * `formData` : un écran du compte connecté resté monté dessous (l'accueil
 * parent) demande SES tarifs sans email, et la dernière réponse arrivée gagne.
 * Un parent connecté sur un enfant qui ouvrait le lien de vente d'un autre
 * lisait la formule de l'enfant connecté, et la page plantait quand celui-ci
 * n'en avait pas.
 *
 * Le hook ne rend donc que la réponse à SA requête (`pricingRequestEmail`), et
 * la garde une fois vue. Il ne la redemande que s'il ne l'a jamais vue (les
 * deux réponses arrivées dans le même rendu). Sans elle, la page attend :
 * jamais la formule ni le prix d'un autre enfant.
 */
export const useGetFastTrackUserOnMount = (props, onSuccess = () => {}) => {
    const formData = useSelector((state) => state.data.formData)
    // Le statut de la DERNIÈRE requête partie sur ce tiroir, avec son email.
    const slot = useSelector((state) => state.api.getSubscribePricing)

    const emailB64 = props.route?.params?.user ?? ""
    const email = base64.decode((emailB64 || "").replace(/-/g, ""))

    // Une requête est celle du lien quand elle a été faite pour son email.
    const isForLink = (requestEmail) => !email || requestEmail === email
    const isOwn = isForLink(formData?.pricingRequestEmail)
    const isLoading = !!slot?.request
    // La requête ou l'échec d'une autre requête (celle de l'accueil) ne
    // concerne pas le lien.
    const isLoadingOwn = isLoading && isForLink(slot.urlParams?.email)
    const hasError = !!slot?.error && isForLink(slot.urlParams?.email)

    // La réponse du lien, gardée une fois vue : la remplacer par celle d'un
    // autre écran ne fait ni recharger la page ni repartir une requête.
    const ownData = useRef(null)
    useEffect(() => {
        if (isOwn && formData?.user) ownData.current = { email, formData }
    }, [isOwn, formData, email])
    const state = isOwn
        ? formData
        : ownData.current?.email === email
          ? ownData.current.formData
          : null

    const { initialLoadingHack } = useRequestOnMount(
        props.apiRequest,
        subscriptionService.getSubscribePricing,
        { queryParams: { email } },
        (data, allData, urlParams, routeParams) => {
            if (isForLink(urlParams?.email))
                onSuccess(data, allData, urlParams, routeParams)
        }
    )

    const refetches = useRef(0)
    useEffect(() => {
        // Une requête du lien en échec se montre (écran d'erreur), elle ne se
        // relance pas : rien ne l'a remplacée.
        if (state || isLoading || initialLoadingHack || hasError) return
        if (refetches.current < MAX_REFETCHES) {
            props.apiRequest(
                subscriptionService.getSubscribePricing,
                undefined,
                undefined,
                { email }
            )
        } else if (refetches.current === MAX_REFETCHES) {
            notifyBugsnag(
                new Error("Lien d'inscription : la réponse du lien reste remplacée par celle d'un autre écran"),
                // Des identifiants, pas les emails de la famille.
                { route: props.route?.name, loaded_user_id: formData?.user?.id }
            )
        }
        refetches.current += 1
    }, [!!state, isLoading, initialLoadingHack, hasError])

    const subscriptionStatus = state?.user?.subscription_state?.status
    return {
        state,
        email,
        emailB64,
        subscriptionMetadata: state?.subscriptionMetadata,
        paymentLink: state?.invoiceShortUrl,
        user: state?.user,
        initialLoading:
            initialLoadingHack || isLoadingOwn || (!state && !hasError),
        hasFinishedFastTrack: !!subscriptionStatus && !["on_generic_trial", "ended", "expired_trial", "-"]?.includes(subscriptionStatus),
    }
}
